import type { ReactNode } from 'react'
import { X } from 'lucide-react'
import { createPortal } from 'react-dom'
import classNames from 'classnames'

import Button from '@/elements/Button'
import Text from '@/elements/Text'
import Flex from '@/elements/Flex'
import useBreakpoints from '@/hooks/useBreakpoints'

import './Modal.scss'

type ButtonVariant = 'primary' | 'secondary' | 'accent' | 'transparent' | 'danger'

interface ModalProps {
  title?: string | ReactNode
  isOpen: boolean
  onCancel: () => void
  onSave?: () => void
  preventClickOutsideClose?: boolean
  children: ReactNode
  saveLabel?: string
  cancelLabel?: string
  saveButtonVariant?: ButtonVariant
  className?: string
  style?: React.CSSProperties
  disabled?: boolean
}

function Modal({
  title,
  isOpen,
  onCancel,
  onSave,
  preventClickOutsideClose,
  children,
  saveLabel = 'Save',
  cancelLabel = 'Cancel',
  saveButtonVariant = 'accent',
  className,
  style,
  disabled,
}: ModalProps) {
  const { isMobile } = useBreakpoints()
  if (!isOpen) return null

  return createPortal(
    <div
      className='modal-overlay'
      onClick={() => {
        if (!preventClickOutsideClose) {
          onCancel()
        }
      }}
    >
      <div
        className={classNames('modal', className)}
        style={style}
        onClick={e => e.stopPropagation()}
      >
        <Button variant='iconBtn' onClick={onCancel}><X /></Button>
        {title && (
          <Flex className='modal-header'>
            {typeof title === 'string' ? <Text variant='h3'>{title}</Text> : title}
          </Flex>
        )}
        <Flex wfull className='modal-body'>{children}</Flex>
        {onSave && (
          <Flex wfull className='modal-footer' inline={!isMobile} gap={2}>
            <Button variant='secondary' onClick={onCancel}>{cancelLabel}</Button>
            <Button variant={saveButtonVariant} onClick={onSave} disabled={disabled}>{saveLabel}</Button>
          </Flex>
        )}
      </div>
    </div>,
    document.body
  )
}

export default Modal
